import { Component, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Clipboard } from '@angular/cdk/clipboard';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CopyRialButton } from '../../shared/components/copy-rial-button/copy-rial-button';
import { PaymentService } from '../payment.service';
import { PayPaymentRequestResponse, PaymentDetails, PaymentRequestDetail } from '../payment.type';
import { PaymentSourceFields } from '../payment-source-fields/payment-source-fields';
import { MAX_PROOFS, PROOF_ACCEPT, addProofFiles } from '../proof-files';

export interface PayRequestDialogData {
  request: PaymentRequestDetail;
}

/**
 * Pays one payment request, the way it is done at the bank or wallet: copy the
 * card number / Shaba / address, check the holder's name (bank only), copy the
 * amount, send it, then confirm here. Confirming records the payment of exactly what the request
 * covers, already marked paid. The backend refuses anything but the request's
 * own amount and refuses a second payment, so a double click cannot pay twice.
 */
@Component({
  selector: 'app-pay-request-dialog',
  imports: [
    CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatProgressSpinnerModule, MatTooltipModule, CopyRialButton, PaymentSourceFields,
  ],
  templateUrl: './pay-request-dialog.html',
  styleUrl: './pay-request-dialog.scss',
})
export class PayRequestDialog implements OnDestroy {
  private dialogRef = inject(MatDialogRef<PayRequestDialog, PayPaymentRequestResponse | null>);
  private paymentService = inject(PaymentService);
  private clipboard = inject(Clipboard);
  private snackBar = inject(MatSnackBar);
  readonly data = inject<PayRequestDialogData>(MAT_DIALOG_DATA);
  readonly accept = PROOF_ACCEPT;
  readonly room = MAX_PROOFS;

  readonly request = this.data.request;
  readonly isWallet = this.request.payout_kind === 'Wallet';
  readonly account = this.request.payout_account || '';
  readonly holderName = this.request.payout_holder || '';
  readonly accountLabel = this.isWallet ? 'Wallet address' : this.account.startsWith('IR') ? 'Shaba' : 'Card number';

  note = '';
  payment: PaymentDetails = { source_account_id: '', bank_fee: 0, transaction_ref: '' };
  sent = false;
  paying = false;
  error = '';
  proofs: { file: File; url: string }[] = [];
  fileError = '';

  /** 6037991234567890 -> 6037 9912 3456 7890, for reading it against the bank's screen. Addresses stay as they are. */
  get displayAccount(): string {
    if (this.isWallet) return this.account;
    return this.account.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
  }

  get rial(): number {
    return Math.round(this.request.amount * 10);
  }

  copy(text: string, what: string): void {
    const value = text.replace(/\s+/g, '');
    if (this.clipboard.copy(value)) {
      this.snackBar.open(`${what} copied`, undefined, { duration: 1500 });
    } else {
      this.snackBar.open('Could not copy — select it manually', 'Close', { duration: 4000 });
    }
  }

  onFilesSelected(event: Event): void {
    const current = this.proofs.map(p => p.file);
    const { files, error } = addProofFiles(event.target as HTMLInputElement, current, this.room);
    this.fileError = error;
    for (const file of files.slice(current.length)) {
      this.proofs.push({ file, url: URL.createObjectURL(file) });
    }
  }

  removeFile(index: number): void {
    URL.revokeObjectURL(this.proofs[index].url);
    this.proofs.splice(index, 1);
    this.fileError = '';
  }

  pay(): void {
    if (!this.sent || this.paying || !this.payment.source_account_id) return;
    this.paying = true;
    this.error = '';
    this.dialogRef.disableClose = true;
    this.paymentService.payPaymentRequest(
      this.request.id, this.request.amount, this.payment, this.note, this.proofs.map(p => p.file),
    ).subscribe({
      next: res => this.dialogRef.close(res),
      error: err => {
        this.paying = false;
        this.dialogRef.disableClose = false;
        this.error = err?.error?.error ?? 'Failed to record the payment';
      },
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  ngOnDestroy(): void {
    for (const p of this.proofs) URL.revokeObjectURL(p.url);
  }
}
